"use client";

import { useState, useRef, useCallback } from "react";
import { carouselData } from "@/features/landing/data";

const shortDescs: Record<string, string> = {
  "Daily Drip": "3 killer outfits from your own closet. Every morning.",
  "Look Lab": "Mix, match & discover combos you never knew you had.",
  "Shopping Quest": "Find the one piece your wardrobe is actually missing.",
  "Your Pods": "Organize fits by vibe, trip, or mood. Like playlists.",
};

export default function LandingFeaturesCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const startX = useRef(0);
  const endX = useRef(0);
  const dragging = useRef(false);
  const pillRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
    pillRefs.current[index]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, []);

  const onEnd = () => {
    if (!dragging.current) return;
    const d = startX.current - endX.current;
    if (Math.abs(d) > 30) {
      if (d > 0) goToSlide(Math.min(currentSlide + 1, carouselData.length - 1));
      else goToSlide(Math.max(currentSlide - 1, 0));
    }
    startX.current = 0;
    endX.current = 0;
    dragging.current = false;
  };

  return (
    <section className="py-12 sm:py-16 bg-[#fafafa]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="px-6 mb-6 sm:mb-8 text-center">
          <p className="text-[11px] tracking-[0.25em] uppercase text-[#6b6b6b] font-winky mb-2">
            Features
          </p>
          <h2 className="text-[1.75rem] sm:text-[2.25rem] font-bold text-[#2d2d2d] font-winky leading-tight">
            What Hoku can do
          </h2>
        </div>

        {/* Pill nav */}
        <div className="flex gap-2 mb-6 sm:mb-8 overflow-x-auto no-scrollbar px-6 sm:justify-center">
          {carouselData.map((item, index) => (
            <button
              key={item.id}
              ref={(el) => { pillRefs.current[index] = el; }}
              onClick={() => goToSlide(index)}
              className={`flex-shrink-0 px-5 py-2 rounded-full font-winky text-[12px] transition-all duration-300 border whitespace-nowrap ${
                index === currentSlide
                  ? "bg-[#2d2d2d] text-white border-[#2d2d2d] shadow-[2px_2px_0_#4c4c4c]"
                  : "bg-white text-[#6b6b6b] border-[#e0e0e0] hover:border-[#aaa]"
              }`}
            >
              {item.title}
            </button>
          ))}
        </div>

        {/* Swipeable carousel */}
        <div className="relative overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-[cubic-bezier(.25,.1,.25,1)]"
            style={{
              transform: `translateX(calc(7.5% - ${currentSlide * 85}%))`,
              cursor: dragging.current ? "grabbing" : "grab",
              userSelect: "none",
            }}
            onMouseDown={(e) => { dragging.current = true; startX.current = e.clientX; endX.current = e.clientX; e.preventDefault(); }}
            onMouseMove={(e) => { if (dragging.current) endX.current = e.clientX; }}
            onMouseUp={onEnd}
            onMouseLeave={() => { if (dragging.current) onEnd(); }}
            onTouchStart={(e) => { dragging.current = true; startX.current = e.touches[0].clientX; endX.current = e.touches[0].clientX; }}
            onTouchMove={(e) => { if (dragging.current) endX.current = e.touches[0].clientX; }}
            onTouchEnd={onEnd}
          >
            {carouselData.map((item, index) => (
              <div key={item.id} className="flex-shrink-0 px-3" style={{ width: "85%" }}>
                <div
                  className={`rounded-3xl overflow-hidden transition-all duration-500 border border-white/50 ${
                    index === currentSlide
                      ? "scale-100 opacity-100 shadow-xl"
                      : "scale-[0.95] opacity-30"
                  }`}
                  style={{ backgroundColor: item.bgColor }}
                >
                  <div className="h-[360px] sm:h-[420px] flex items-center justify-center overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.altText || item.title}
                      width={512}
                      height={856}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </div>

                  <div className="bg-white px-6 py-5">
                    <h3 className="text-lg font-bold text-[#2d2d2d] font-winky mb-1">
                      {item.title}
                    </h3>
                    <p className="text-[#6b6b6b] text-[13px] leading-relaxed font-patrick">
                      {shortDescs[item.title] || item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-1.5 mt-6">
          {carouselData.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`rounded-full transition-all duration-300 ${
                currentSlide === index
                  ? "w-7 h-2 bg-[#2d2d2d]"
                  : "w-2 h-2 bg-[#2d2d2d]/20"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
